import CollectionDataGrid from '@/components/CollectionDataGrid'
import SimpleTable from '@/components/SimpleTable'
import { pbUpsert, useCollection } from '@/pb'
import { arrayToObject, interval, objectMap, ruDateTimeSec, useInputState } from '@/utils'
import { Alert, Button, DialogActions, DialogContent, DialogTitle, FormControl, InputAdornment, InputLabel, LinearProgress, MenuItem, Select, TextField } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

const blockUnblock = (deviceId, blocked) => () => {
  pb.collection('devices').update(deviceId, { blocked })
}

const BlockButton = ({ id, blocked }) => (
  <Button
    onClick={blockUnblock(id, !blocked)}
    variant={blocked ? 'contained' : 'text'}
    color={blocked ? 'error' : 'primary'}
  >
    {blocked ? 'Разблокировать' : 'Заблокировать'}
  </Button>
)

const errorsColumns = [
  { field: 'desc', headerName: 'Ошибка' },
  { field: 'err', headerName: 'Детали' },
]

const Editor = ({ row, onClose }) => {
  const [client, setClient] = useInputState(row.client || 'none')
  const [clients, clientsLoaded] = useCollection('clients')

  const errorsRows = useMemo(() => {
    if (!row.errors) return []
    return row.errors.split('\n').map(v => v.split(':', 2)).map(([desc, err]) => ({ id: desc, desc, err }))
  }, [row.errors])

  if (!clientsLoaded) return <LinearProgress />

  return <>
    <DialogTitle>Устройство</DialogTitle>
    <DialogContent className="editorForm">
      <TextField label={fields.deviceId.headerName} value={row.deviceId} disabled />
      <OnlineStatus date={row.onlineAt} hasError={row.errors} render={onlineAtTextFieldRender} />

      <FormControl>
        <InputLabel id="client">{fields.clientName.headerName}</InputLabel>
        <Select
          labelId="client"
          value={client}
          label={fields.clientName.headerName}
          onChange={setClient}
        >
          <MenuItem value="none"><em>Производитель</em></MenuItem>
          {Object.entries(clients).map(([id, { name }]) =>
            <MenuItem value={id}>{name}</MenuItem>
          )}
        </Select>
      </FormControl>
      <BlockButton id={row.id} blocked={row.blocked} />
      {!!row.errors &&
        <SimpleTable rows={errorsRows} columns={errorsColumns} component={Alert} severity="error" icon={false} />
      }
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Отмена</Button>
      <Button onClick={() => pbUpsert('devices', { id: row.id, client: client === 'none' ? '' : client }).then(onClose)} variant="contained">Сохранить</Button>
    </DialogActions>
  </>
}

const onlineTimeoutMs = 15000 // 15 sec

const OnlineStatus = ({ date, hasError, render }) => {
  const timestamp = useMemo(() => new Date(date).getTime(), [date])
  const [online, setOnline] = useState(false)
  useEffect(() => {
    setOnline(Date.now() - timestamp < onlineTimeoutMs)
    return interval(() => {
      setOnline(Date.now() - timestamp < onlineTimeoutMs)
    }, onlineTimeoutMs)
  }, [timestamp])
  const statusIndicator = hasError ? (online ? '🔴 ' : '⚫ ') : (online ? '🟢 ' : '⚪ ')
  return render ? render(statusIndicator, timestamp) : statusIndicator
}

const onlineAtColumnRender = (statusIndicator, timestamp) => statusIndicator + ruDateTimeSec(timestamp)
const onlineAtTextFieldRender = (statusIndicator, timestamp) => {
  const slotProps = useMemo(() => ({
    input: {
      startAdornment: <InputAdornment position="start">{statusIndicator}</InputAdornment>,
    },
  }), [statusIndicator])
  return (
    <TextField
      disabled
      label={fields.onlineAt.headerName}
      value={ruDateTimeSec(timestamp)}
      slotProps={slotProps}
    />
  )
}

const columns = [
  { field: 'deviceId', headerName: 'ID Устройства', width: 200 },
  {
    field: 'onlineAt', headerName: 'Был в сети', width: 180,
    renderCell: ({ value, row }) => <OnlineStatus date={value} hasError={row.errors} render={onlineAtColumnRender} />
  },
  { field: 'clientName', headerName: 'В распоряжении', width: 200 },
  {
    field: 'blocked', headerName: 'Блокировка', width: 200,
    renderCell: ({ id, value }) => <BlockButton id={id} blocked={value} />
  },
  { field: 'errors', headerName: 'Ошибки', width: 200 },
  { field: 'deviceType', headerName: 'Тип', width: 200 },
  { field: 'hardwareVersion', headerName: 'Версия', width: 200 },
  { field: 'softwareVersion', headerName: 'Версия ПО', width: 200 },
]
const fields = arrayToObject(columns, 'field')

export default function Devices({ title }) {

  const [devices, devicesLoaded] = useCollection('devices')
  const [clients, clientsLoaded] = useCollection('clients')
  const loaded = devicesLoaded && clientsLoaded


  const devicesWithClients = useMemo(() => {
    return objectMap(devices, device => ({
      ...device,
      clientName: clients[device.client]?.name,
    }))
  }, [devices, clients])

  return (
    <CollectionDataGrid
      collectionName={title}
      collection={devicesWithClients}
      columns={columns}
      loading={!loaded}
      editor={Editor}
      creatable={false}
    />
  )

}